import React, { useRef, useEffect, useContext } from "react";

import Section from "../../baseComponents/Section";

import PageHeading from "../../baseComponents/PageHeading";
import SLBatchHeader, { ISLBatchHeaderRef } from "./SLBatchHeader";

import { SLEntriesError, FatalError } from "../../services/ErrorHelpers";
import { useLocation, useHistory } from "react-router-dom";
import { assembleSLEntry, saveBatchEntry } from "./SLBatchActionsRequests";
import Attachments, { IAttachmentEntryFormRef } from "../Attachments";
import { IAttachment } from "../../schemas/SLAttachment";
import { getStateObjects, redirectMeToTheOrigin } from "../../services/UrlHelpers";

import SLBatchValidationErrors from "./SLBatchValidationErrors";
import { LoggingContext } from "../../contexts/LoggingContext";
import { UserContext } from "../../contexts/UserContext";
import { SLLoadingStatus, delay } from "../../utilities/utilities";
import SLBatchActionBar, { AllSLBatchActions, BatchActionBarAndNotificationsRef } from "./SLBatchActionBar";
import { SLCreateSLBatchProvider } from "../../contexts/SLCreateSLBatchContext";
import { ISLBatchResponse } from "./SLBatchSchemas";
import { useDataFromSubledger } from "../../hooks/useDataFromSubledger";

export interface IDialogBlockingState {
  hideDialogClearForm?: boolean;
  hideDialogPost?: boolean;
}

function baseCommandBuilder(
  onNotification: BatchActionBarAndNotificationsRef["setMessage"],
  resetNotifications: BatchActionBarAndNotificationsRef["reset"],
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>
) {
  const baseCommand = async (commandNameStr: string, callback: () => Promise<string[]>, redirect = false) => {
    resetNotifications();
    await delay(200);
    onNotification("info", [`${commandNameStr} is pending.`]);
    try {
      // do command here
      const messages = await callback();
      resetNotifications();
      await delay(200);
      onNotification("success", messages);
      if (redirect && messages.length > 0) {
        redirectMeToTheOrigin(history, location, 5000);
      }
    } catch (e) {
      // handle errors here
      resetNotifications();
      await delay(200);
      if (e instanceof SLEntriesError) {
        onNotification("errors", e.messages);
      } else {
        onNotification("errors", [`${commandNameStr} failed.`, e.message]);
      }
    }
  };
  return baseCommand;
}

type BatchCommands = AllSLBatchActions;

const SLBatch: React.FC = () => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please setup ApplicationInsights Provider for SLBatch");
  }
  const { accessToken } = useContext(UserContext);
  const [endpointData, setEntry] = useDataFromSubledger("SLBatch");
  const _notificationsRef = useRef<BatchActionBarAndNotificationsRef>(null);
  const attachmentsRef = useRef<IAttachmentEntryFormRef>(null);
  const location = useLocation();
  const history = useHistory();

  // hear ye human: this component rules all of slbatch
  // for the actions all we need is the entry id, refGUID and rowVer, the data comes from subcomponents of slbatch
  // and so we use a ref, to avoid triggering any re renderings we a variable changes
  // do not use useState in this component unless you want to be in a world of pain
  const entry = useRef<{
    entryId: number;
    refGUID: string | null;
    rowVer: ArrayBuffer | null;
  }>({
    entryId: 0,
    refGUID: null,
    rowVer: null
  });

  const headerRef = useRef<ISLBatchHeaderRef>(null);

  const onNotification: BatchActionBarAndNotificationsRef["setMessage"] = (type, messages) => {
    if (_notificationsRef.current) {
      _notificationsRef.current.setMessage(type, messages);
    }
  };

  const resetNotifications: BatchActionBarAndNotificationsRef["reset"] = () => {
    if (_notificationsRef.current) {
      _notificationsRef.current.reset();
    }
  };

  useEffect(() => {
    if (endpointData.entry !== null) {
      const entryData = endpointData.entry as ISLBatchResponse;
      entry.current = {
        entryId: entryData.id,
        refGUID: entryData.refGUID,
        rowVer: entryData.rowVer
      };
    } else {
      entry.current = {
        entryId: 0,
        refGUID: null,
        rowVer: null
      };
    }
  }, [endpointData.entry]);

  useEffect(() => {
    if (endpointData.loadingStatus === SLLoadingStatus.Resolved) {
      if (endpointData.entry === null) {
        resetNotifications();
      }
    } else if (endpointData.loadingStatus === SLLoadingStatus.Rejected) {
      if (endpointData.notification) {
        onNotification(endpointData.notification.type, endpointData.notification.messages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointData.loadingStatus]);

  const onCommand = (commandName: AllSLBatchActions) => {
    const validCommandForBatch = commandName as BatchCommands;
    const exceptionHandler = baseCommandBuilder(onNotification, resetNotifications, history, location);

    const saveAndLoad = async () => {
      const fields = await assembleSLEntry(accessToken, headerRef, attachmentsRef);

      const payload = await saveBatchEntry(
        entry.current.entryId,
        fields.entry,
        fields.batchFile as IAttachment,
        accessToken,
        entry.current.refGUID,
        entry.current.rowVer
      );
      setEntry(payload.batchEntry);
      if (location.pathname === "/slbatch") {
        const st = getStateObjects(location);
        history.push(`/slbatch/${payload.batchEntry.refGUID}`, st ? st.state : undefined);
      }

      return payload;
    };

    try {
      switch (validCommandForBatch) {
        case "post":
          exceptionHandler(
            "Post",
            async () => {
              const payload = await saveAndLoad();
              return payload.messages;
            },
            true
          );
          break;
        case "clearForm":
          resetNotifications();
          setEntry(null);
          break;
      }
    } catch (e) {
      console.error(e);
      appInsights.trackException({ exception: e });
    }
  };

  return (
    <SLCreateSLBatchProvider endpointData={endpointData}>
      <div className="content">
        <PageHeading onClose={() => redirectMeToTheOrigin(history, location, 0)}>Subledger Batch</PageHeading>
        <SLBatchActionBar ref={_notificationsRef} onCommand={onCommand}>
          <Section label="Batch">
            <SLBatchHeader ref={headerRef} />
          </Section>
          <Section label="Attachments">
            <Attachments ref={attachmentsRef} onNotification={onNotification} />
          </Section>
          <Section label="Validation Errors">
            <SLBatchValidationErrors />
          </Section>
        </SLBatchActionBar>
      </div>
    </SLCreateSLBatchProvider>
  );
};

SLBatch.displayName = "SLBatch";

export default SLBatch;
